// frontend/src/services/lawApi.js
import axios from 'axios';
import { getApiBaseURL } from '../utils/api';

const BASE_URL = getApiBaseURL();
const LAW_DETAIL_CACHE_PREFIX = 'law_detail_v8_';
const LAW_DETAIL_META_VERSION = 8;

class LawApiService {
    constructor() {
        this._relatedItemsCache = new Map();
        this._legalRefsCache = new Map();
        this._relatedItemsInflight = new Map();
        this._legalRefsInflight = new Map();
        this.api = axios.create({
            baseURL: BASE_URL,
            timeout: 30000, // 10초에서 30초로 증가
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // 간소화된 응답 인터셉터
        this.api.interceptors.response.use(
            response => response,
            error => {
                console.error('API Error:', error.message);
                return Promise.reject(error);
            }
        );
    }

    // 법령 목록 조회
    async getLawList() {
        try {
            // BASE_URL이 비어 있으면 현재 도메인의 /api를 사용한다.
            // 프론트와 API가 같은 도메인으로 배포된 환경에서는 이것이 정상 동작이다.
            const response = await this.api.get('/api/laws');
            
            // HTML이 반환된 경우 (잘못된 URL)
            if (typeof response.data === 'string' && response.data.trim().startsWith('<!')) {
                console.error('API가 HTML을 반환했습니다. 백엔드 URL이 올바른지 확인하세요:', BASE_URL);
                throw new Error('백엔드 API URL이 잘못되었습니다. REACT_APP_API_URL 환경 변수를 확인하세요.');
            }
            
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('법령 목록 조회 오류:', error);
            console.error('API Base URL:', BASE_URL);
            throw error;
        }
    }

    // 법령 검색
    async searchLaws(keyword) {
        try {
            const response = await this.api.get(`/api/laws/search?keyword=${encodeURIComponent(keyword)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    _isStaleLawDetailCache(data) {
        if (!data || typeof data !== 'object') return true;
        return (data.meta_version || 1) < LAW_DETAIL_META_VERSION;
    }

    _getLawDetailCacheKey(lawId) {
        return `${LAW_DETAIL_CACHE_PREFIX}${lawId}`;
    }

    _removeLegacyLawDetailCache(lawId) {
        try {
            localStorage.removeItem(`law_detail_${lawId}`);
        } catch {
            // ignore
        }
    }

    // localStorage에서 캐시된 법령 데이터 조회
    _getCachedLawDetail(lawId) {
        try {
            this._removeLegacyLawDetailCache(lawId);
            const cacheKey = this._getLawDetailCacheKey(lawId);
            const cached = localStorage.getItem(cacheKey);
            
            if (!cached) {
                return null;
            }
            
            const { data, timestamp } = JSON.parse(cached);
            if (this._isStaleLawDetailCache(data)) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            const now = Date.now();
            const cacheAge = now - timestamp;
            const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24시간 (밀리초)
            
            // 24시간 이내면 캐시 사용
            if (cacheAge < CACHE_DURATION) {
                console.log(`[캐시 사용] 법령 ${lawId} - 캐시 나이: ${Math.round(cacheAge / 1000 / 60)}분`);
                return data;
            }
            
            // 캐시 만료됨
            console.log(`[캐시 만료] 법령 ${lawId} - 캐시 나이: ${Math.round(cacheAge / 1000 / 60 / 60)}시간`);
            localStorage.removeItem(cacheKey);
            return null;
        } catch (error) {
            console.error('캐시 읽기 오류:', error);
            return null;
        }
    }

    // 법령 데이터를 localStorage에 캐시
    _setCachedLawDetail(lawId, data) {
        try {
            const cacheKey = this._getLawDetailCacheKey(lawId);
            const cacheData = {
                data,
                timestamp: Date.now()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log(`[캐시 저장] 법령 ${lawId}`);
        } catch (error) {
            console.error('캐시 저장 오류:', error);
            // localStorage 용량 초과 시 오래된 캐시 삭제 시도
            if (error.name === 'QuotaExceededError') {
                this._clearOldCache();
            }
        }
    }

    // 오래된 캐시 정리 (가장 오래된 것부터 삭제)
    _clearOldCache() {
        try {
            const cacheKeys = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith('law_detail_') || key.startsWith(LAW_DETAIL_CACHE_PREFIX))) {
                    cacheKeys.push(key);
                }
            }
            
            // 타임스탬프 기준으로 정렬
            const cacheWithTime = cacheKeys.map(key => {
                try {
                    const cached = localStorage.getItem(key);
                    const { timestamp } = JSON.parse(cached);
                    return { key, timestamp };
                } catch {
                    return { key, timestamp: 0 };
                }
            }).sort((a, b) => a.timestamp - b.timestamp);
            
            // 가장 오래된 10개 삭제
            cacheWithTime.slice(0, 10).forEach(({ key }) => {
                localStorage.removeItem(key);
                console.log(`[캐시 정리] ${key} 삭제`);
            });
        } catch (error) {
            console.error('캐시 정리 오류:', error);
        }
    }

    // 법령 상세 내용 조회 (24시간 캐싱 적용)
    async getLawDetail(lawId, { refresh = false } = {}) {
        // 1. localStorage 캐시 확인 (24시간 이내, 최신 스키마)
        if (!refresh) {
            const cachedData = this._getCachedLawDetail(lawId);
            if (cachedData) {
                return cachedData;
            }
        }
        
        // 2. 캐시가 없거나 만료되었으면 서버에서 가져오기
        try {
            const refreshQuery = refresh ? '?refresh=1' : '';
            const response = await this.api.get(`/api/laws/${lawId}/detail${refreshQuery}`);
            const data = response.data;
            
            // 에러 응답 확인
            if (data && data.error) {
                // 에러가 있어도 기본 구조는 반환
                const errorData = {
                    id: lawId,
                    title: data.title || '법령 정보',
                    chapters: data.chapters || [],
                    error: data.error
                };
                // 에러 데이터도 캐시 (짧은 시간 동안 같은 에러 반복 방지)
                this._setCachedLawDetail(lawId, errorData);
                return errorData;
            }
            
            // 데이터 구조 검증
            if (!data.chapters || !Array.isArray(data.chapters)) {
                data.chapters = [];
            }

            // 성공한 데이터는 캐시에 저장
            this._setCachedLawDetail(lawId, data);
            return data;
        } catch (error) {
            // 네트워크 오류나 서버 오류 시에도 기본 구조 반환
            if (error.response && error.response.data) {
                const errorData = {
                    id: lawId,
                    title: '법령 정보',
                    chapters: [],
                    error: error.response.data.error || '법령을 불러오는데 실패했습니다.'
                };
                // 에러 데이터도 캐시 (짧은 시간 동안 같은 에러 반복 방지)
                this._setCachedLawDetail(lawId, errorData);
                return errorData;
            }
            throw error;
        }
    }

    // 법령의 특정 조항 조회
    async getArticleDetail(lawId, articleId) {
        try {
            const response = await this.api.get(`/api/laws/${lawId}/articles/${articleId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // 법령체계도 조회
    async getLawHierarchy(mst) {
        try {
            const response = await this.api.get(`/api/laws/hierarchy/${mst}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
    
    // 통합 검색
    async search(query) {
        try {
            const response = await this.api.get(`/api/search?query=${encodeURIComponent(query)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    _relatedCacheKey(lawId, articleNumber) {
        return `${lawId}:${articleNumber}`;
    }

    peekRelatedItems(lawId, articleNumber) {
        return this._relatedItemsCache.get(this._relatedCacheKey(lawId, articleNumber)) ?? null;
    }

    peekLegalReferences(lawId, articleNumber) {
        return this._legalRefsCache.get(this._relatedCacheKey(lawId, articleNumber)) ?? null;
    }

    _getInflight(map, cacheKey) {
        const pending = map.get(cacheKey);
        return pending ? pending : null;
    }

    // 조항의 연관 항목 조회 (스냅샷 — 세션 캐시)
    async getRelatedItems(lawId, articleNumber) {
        const cacheKey = this._relatedCacheKey(lawId, articleNumber);
        if (this._relatedItemsCache.has(cacheKey)) {
            return this._relatedItemsCache.get(cacheKey);
        }
        const inflight = this._getInflight(this._relatedItemsInflight, cacheKey);
        if (inflight) return inflight;

        const request = this.api
            .get(`/api/laws/${lawId}/articles/${articleNumber}/related`)
            .then((response) => {
                this._relatedItemsCache.set(cacheKey, response.data);
                return response.data;
            })
            .finally(() => {
                this._relatedItemsInflight.delete(cacheKey);
            });
        this._relatedItemsInflight.set(cacheKey, request);
        return request;
    }

    /** 판례·법령해석 등 — DB 스냅샷만 (세션 캐시). force_compute 금지 */
    async getLegalReferences(lawId, articleNumber, options = {}) {
        const cacheKey = this._relatedCacheKey(lawId, articleNumber);
        if (!options.bypassCache && this._legalRefsCache.has(cacheKey)) {
            return this._legalRefsCache.get(cacheKey);
        }
        if (options.bypassCache) {
            this._legalRefsCache.delete(cacheKey);
        }
        const inflight = this._getInflight(this._legalRefsInflight, cacheKey);
        if (inflight) return inflight;

        const request = this.api
            .get(`/api/laws/${lawId}/articles/${articleNumber}/legal-references`)
            .then((response) => {
                this._legalRefsCache.set(cacheKey, response.data);
                return response.data;
            })
            .finally(() => {
                this._legalRefsInflight.delete(cacheKey);
            });
        this._legalRefsInflight.set(cacheKey, request);
        return request;
    }

    peekRelatedTop(lawId, articleNumber, options = {}) {
        const limit = options.limit || 100;
        return this._relatedTopCache?.get(`${this._relatedCacheKey(lawId, articleNumber)}:${limit}`) ?? null;
    }

    /** 수록+법제처 합산 관련 자료 — DB 스냅샷만 */
    async getRelatedTop(lawId, articleNumber, options = {}) {
        if (!this._relatedTopCache) this._relatedTopCache = new Map();
        if (!this._relatedTopInflight) this._relatedTopInflight = new Map();
        const limit = options.limit || 100;
        const cacheKey = `${this._relatedCacheKey(lawId, articleNumber)}:${limit}`;
        if (!options.bypassCache && this._relatedTopCache.has(cacheKey)) {
            return this._relatedTopCache.get(cacheKey);
        }
        if (options.bypassCache) {
            this._relatedTopCache.delete(cacheKey);
        }
        const inflight = this._getInflight(this._relatedTopInflight, cacheKey);
        if (inflight) return inflight;

        const request = this.api
            .get(`/api/laws/${lawId}/articles/${articleNumber}/related-top`, {
                params: { limit },
            })
            .then((response) => {
                this._relatedTopCache.set(cacheKey, response.data);
                return response.data;
            })
            .finally(() => {
                this._relatedTopInflight.delete(cacheKey);
            });
        this._relatedTopInflight.set(cacheKey, request);
        return request;
    }

    async getArticleRelatedCounts(lawId) {
        const response = await this.api.get(`/api/laws/${lawId}/article-related-counts`);
        return response.data;
    }

    async getPrecedentDetail(precId) {
        const response = await this.api.get(`/api/legal-references/precedents/${precId}`);
        return response.data;
    }

    async getInterpretationDetail(source, interpId) {
        const response = await this.api.get(
            `/api/legal-references/interpretations/${source}/${interpId}`
        );
        return response.data;
    }

    async getAdminRuleDetail(ruleId) {
        const response = await this.api.get(`/api/legal-references/admin-rules/${ruleId}`);
        return response.data;
    }

    async getIaciacDetail(decisionId) {
        const response = await this.api.get(`/api/legal-references/iaciac/${decisionId}`);
        return response.data;
    }

    async getLawChangeHistory(days = 14) {
        const response = await this.api.get('/api/laws/updates/change-history', {
            params: { days },
        });
        return response.data;
    }

    async getDailyArticleRevisions(days = 7, lawId = '') {
        const response = await this.api.get('/api/laws/updates/daily-article-revisions', {
            params: { days, law_id: lawId || undefined },
        });
        return response.data;
    }
}

export const lawApiService = new LawApiService();
